import { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Button, Dialog, Text, TextInput } from '@opendoor/bricks-next';
import type { TextInput as RNTextInput } from 'react-native';

import { useObservability } from '../../../helpers/observability';
import type { PropertySearchQuery } from '../helpers/useSearchQueries';
import { SAVED_SEARCHES_ENABLED } from './featureFlag';
import { formatSavedSearchName, hasValidLocation, toSavedSearchInput } from './mapping';
import { useSavedSearches } from './useSavedSearches';

export interface SaveSearchButtonProps {
  /**
   * The canonical filter shape for the search being saved. The parent must
   * merge `searchState.getCanonical()` with the active location (market,
   * zoneIds, or polygon vertices) before passing it in; `getCanonical()`
   * intentionally omits location.
   */
  query: PropertySearchQuery;
  variant?: 'primary' | 'secondary';
  size?: 'small' | 'medium';
  onSaved?: (savedSearchId: string) => void;
}

const SAVE_SEARCH_NAME_MAX_LENGTH = 80;
const NO_LOCATION_HINT = 'Pick a market, neighborhood, or area on the map first.';
const GENERIC_ERROR = 'Failed to save search. Please try again.';

// The marketplace doesn't have an in-page login modal, so unauthenticated
// users are bounced to `/login?redirectTo=...` matching `usePropertyReactions`.
const redirectToLogin = () => {
  const redirectTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/login?redirectTo=${encodeURIComponent(redirectTo)}`);
};

export const SaveSearchButton = ({
  query,
  variant = 'secondary',
  size = 'medium',
  onSaved,
}: SaveSearchButtonProps) => {
  const { isLoggedIn, add } = useSavedSearches();
  const { trackEvent } = useObservability();

  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Bricks-next `TextInput` forwards a React Native TextInput ref; we use it
  // to drive focus once the Dialog portal mounts.
  const inputRef = useRef<RNTextInput | null>(null);

  const validLocation = hasValidLocation(query);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    // Microtask defer so the Dialog content has time to mount before we
    // dispatch focus into it.
    queueMicrotask(() => {
      inputRef.current?.focus();
    });
  }, [isOpen]);

  const handleOpenDialog = useCallback(() => {
    if (!isLoggedIn) {
      redirectToLogin();
      return;
    }
    setName(formatSavedSearchName(query));
    setErrorMessage(null);
    setIsOpen(true);
  }, [isLoggedIn, query]);

  const handleClose = useCallback(() => {
    if (submitting) {
      return;
    }
    setIsOpen(false);
  }, [submitting]);

  const handleSubmit = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName || submitting) {
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    const result = await add(toSavedSearchInput(query, trimmedName));
    setSubmitting(false);
    if (result.success) {
      trackEvent('cta-click', 'save-search-created', {
        savedSearchId: result.savedSearch?.id,
        market: query.market,
        zoneIdsCount: query.zoneIds?.length ?? 0,
        verticesCount: query.vertices?.length ?? 0,
      });
      setIsOpen(false);
      onSaved?.(result.savedSearch?.id ?? '');
      return;
    }
    setErrorMessage(result.errorMessage ?? GENERIC_ERROR);
  }, [add, name, onSaved, query, submitting, trackEvent]);

  const trimmedName = name.trim();
  const canSubmit = !submitting && trimmedName.length > 0;
  const triggerHint = validLocation ? undefined : NO_LOCATION_HINT;

  let submitLabel = 'Save';
  if (submitting) {
    submitLabel = 'Saving…';
  }

  if (!SAVED_SEARCHES_ENABLED) {
    return null;
  }

  return (
    <>
      <Button
        analyticsName="save-search-button"
        label="Save search"
        onPress={handleOpenDialog}
        size={size}
        $smallerThanMD={{ size: 'small', flex: 1, minWidth: 0 }}
        variant={variant}
        disabled={!validLocation}
        aria-label={validLocation ? 'Save search' : `Save search. ${triggerHint}`}
      />
      <Dialog
        analyticsName="save-search-dialog"
        isOpen={isOpen}
        setIsOpen={handleClose}
        dismissable={!submitting}
      >
        <Box width="100%" $largerThanSM={{ width: 360 }}>
          <Dialog.Content
            title="Name your saved search"
            description="Give your search a name so you can find it later."
          >
            <Box pt={16} pb={8}>
              <TextInput
                ref={inputRef}
                size="medium"
                title="Search name"
                value={name}
                onChangeText={setName}
                maxLength={SAVE_SEARCH_NAME_MAX_LENGTH}
                analyticsName="save-search-name-input"
                placeholder="My saved search"
              />
              {errorMessage && (
                <Text
                  tag="p"
                  typography="$bodySmall"
                  color="$contentStateErrorDefault"
                  role="alert"
                  mt={8}
                >
                  {errorMessage}
                </Text>
              )}
            </Box>
          </Dialog.Content>
          <Dialog.Actions
            buttonOneProps={{
              analyticsName: 'save-search-cancel',
              label: 'Cancel',
              variant: 'secondary',
              onPress: handleClose,
              disabled: submitting,
            }}
            buttonTwoProps={{
              analyticsName: 'save-search-submit',
              label: submitLabel,
              variant: 'primary',
              onPress: handleSubmit,
              disabled: !canSubmit,
              loading: submitting,
            }}
          />
        </Box>
      </Dialog>
    </>
  );
};
